/**
 * 动态词汇管理API服务
 */

import { request } from './client'
import type { ApiResponse } from './client'
import type {
  DynamicLexeme,
  DynamicLexemeStats,
  ReviewRequest,
  SearchParams
} from '@/types/dynamic-lexemes'

const createEmptyStats = (): DynamicLexemeStats => ({
  total_dynamic_lexemes: 0,
  pending_review_count: 0,
  approved_count: 0,
  rejected_count: 0,
  top_encounter_words: [],
  daily_creation_trend: []
})

export const dynamicLexemeApi = {
  // 获取统计信息
  async getStats(): Promise<DynamicLexemeStats> {
    const response = await request.get<DynamicLexemeStats>('/api/v1/admin/dynamic-lexemes/stats')
    const payload = response.data as ApiResponse<DynamicLexemeStats> & Partial<DynamicLexemeStats>

    if (payload && typeof payload === 'object') {
      if ('data' in payload && payload.data) {
        return payload.data
      }

      // 向后兼容老格式（直接返回统计字段）
      if ('total_dynamic_lexemes' in payload) {
        const {
          total_dynamic_lexemes = 0,
          pending_review_count = 0,
          approved_count = 0,
          rejected_count = 0,
          top_encounter_words = [],
          daily_creation_trend = []
        } = payload as Partial<DynamicLexemeStats>

        return {
          total_dynamic_lexemes,
          pending_review_count,
          approved_count,
          rejected_count,
          top_encounter_words,
          daily_creation_trend
        }
      }
    }

    return createEmptyStats()
  },

  // 获取待审核词汇列表（支持状态过滤）
  async getPendingReview(limit: number = 50, statusFilter?: string): Promise<DynamicLexeme[]> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    if (statusFilter) {
      params.append('status_filter', statusFilter)
    }

    const response = await request.get<DynamicLexeme[]>(`/api/v1/admin/dynamic-lexemes/pending-review?${params}`)
    const payload = response.data as ApiResponse<DynamicLexeme[]> & { data?: DynamicLexeme[] }
    const data = Array.isArray(payload?.data) ? payload.data : []
    return data
  },

  // 审核词汇
  async reviewLexemes(reviewerId: number, reviewRequest: ReviewRequest): Promise<DynamicLexeme[]> {
    const response = await request.post<DynamicLexeme[]>(`/api/v1/admin/dynamic-lexemes/review?reviewer_id=${reviewerId}`, reviewRequest)
    const payload = response.data as ApiResponse<DynamicLexeme[]> & { data?: DynamicLexeme[] }
    return Array.isArray(payload?.data) ? payload.data : []
  },

  // 搜索词汇
  async searchLexemes(params: SearchParams): Promise<ApiResponse<DynamicLexeme[]>> {
    const queryString = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryString.append(key, value.toString())
      }
    })

    const response = await request.get<DynamicLexeme[]>(`/api/v1/admin/dynamic-lexemes/search?${queryString}`)
    const payload = response.data as ApiResponse<DynamicLexeme[]> & {
      data?: DynamicLexeme[]
      total?: number
      page?: number
      page_size?: number
    }

    const data = Array.isArray(payload?.data) ? payload.data : []

    return {
      success: payload?.success ?? true,
      data,
      message: payload?.message,
      pagination: {
        total: payload?.pagination?.total ?? payload?.total ?? data.length,
        current: payload?.pagination?.current ?? payload?.page ?? params.page ?? 1,
        page_size: payload?.pagination?.page_size ?? payload?.page_size ?? params.page_size ?? data.length
      }
    }
  },

  // 获取词汇详情
  async getLexemeDetail(lexemeId: number): Promise<DynamicLexeme | null> {
    const response = await request.get<DynamicLexeme>(`/api/v1/admin/dynamic-lexemes/${lexemeId}`)
    const payload = response.data as ApiResponse<DynamicLexeme> & { data?: DynamicLexeme }
    return payload?.data ?? null
  },

  // 删除词汇
  async deleteLexeme(lexemeId: number, adminId: number, reason?: string): Promise<ApiResponse<Record<string, string>>> {
    const params = new URLSearchParams()
    params.append('admin_id', adminId.toString())
    if (reason) {
      params.append('reason', reason)
    }

    const response = await request.delete<Record<string, string>>(`/api/v1/admin/dynamic-lexemes/${lexemeId}?${params}`)
    const payload = response.data
    return {
      success: payload?.success ?? true,
      data: payload?.data ?? {},
      message: payload?.message,
      pagination: payload?.pagination
    }
  }
}

export default dynamicLexemeApi